import AppContext from 'App/AppContext';
import { IPDropdown, IPSeparator, IPStack, IPText, IPTextField } from 'baseComponents';
import GenericModal from 'components/Dialogs/GenericModal';
import RichTextEditorMCE from 'components/Text/RichTextEditorMCE';
import ScrollableStackItem from 'components/Utils/ScrollableStackItem';
import { globalStackTokensGapSmall, globalTextStylesBold } from 'globalStyles';
import { TFunction } from 'i18next';
import { KPITypes } from 'models/kpi/kpi';
import { KPIDefinitionTextCorrectionMutation } from 'models/kpi/kpiDefinitionRevision';
import { FormTextCorrectionMutation, TextCorrectionImpactPreview } from 'models/tasks/formVersion';
import { TaskStateId } from 'models/tasks/task';
import TaskType from 'models/tasks/taskType';
import TaskTypeFormElement from 'models/tasks/taskTypeFormElement';
import { Fragment, useContext, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiCorrectKPIDefinitionText, apiPreviewKPIDefinitionTextCorrection } from 'services/Api/kpiService';
import { apiCorrectFormText, apiPreviewFormTextCorrection } from 'services/Api/taskService';
import { apiRequest } from 'services/Auth/authConfig';
import AppError from 'utils/appError';
import { newGuid } from 'utils/guid';
import { removeHtmlTags } from 'utils/html';

interface IFormTextCorrectionModalProps {
  taskType: TaskType;
  isOpen: boolean;
  onDismiss: () => void;
  onCorrected: (taskType: TaskType) => void | Promise<void>;
}

type TextCorrectionTargetType = 'form' | 'field' | 'kpi' | 'choice';

interface IFormTextCorrectionTarget {
  key: string;
  type: TextCorrectionTargetType;
  label: string;
  fieldKey?: string;
  kpiId?: number;
  kpiDefinitionRevisionId?: number;
  kpiDefinitionRevisionRowVersion?: string;
  choiceKey?: number;
  languageId: number;
  name: string;
  description?: string;
  tabName?: string;
  instruction?: string;
  expectedResult?: string;
  choiceValue?: string;
  // The question of a success/error definition, and whether this target may correct one at all. It is
  // the definition's own defValue instead of a translated text, which is why it is not in the set
  // above and why it is offered for that one type only.
  question?: string;
  supportsQuestion?: boolean;
}

const formTargetKey = 'form';

const getFieldLabel = (field: TaskTypeFormElement): string => field.name || field.kpi?.name || field.fieldKey || '';

const getCorrectionTargets = (taskType: TaskType, t: TFunction<string[]>): IFormTextCorrectionTarget[] => {
  const formTranslation = taskType.trans[0];
  const targets: IFormTextCorrectionTarget[] = [
    {
      key: formTargetKey,
      type: 'form',
      label: t('forms:Versioning.Correction.Form'),
      languageId: formTranslation?.languageId ?? 0,
      name: formTranslation?.name ?? taskType.name,
      description: formTranslation?.description ?? taskType.description,
      tabName: formTranslation?.tabName ?? taskType.tabName,
    },
  ];
  const addedKPIDefinitions = new Set<string>();

  for (const field of taskType.form ?? []) {
    if (field.kpi && field.kpiId && field.kpiDefinitionRevisionId) {
      const definitionKey = `kpi:${field.kpiId}:${field.kpiDefinitionRevisionId}`;
      if (addedKPIDefinitions.has(definitionKey)) {
        continue;
      }

      addedKPIDefinitions.add(definitionKey);
      const translation = field.kpi.trans[0];
      targets.push({
        key: definitionKey,
        type: 'kpi',
        label: t('forms:Versioning.Correction.KPIDefinition', { name: getFieldLabel(field) }),
        kpiId: field.kpiId,
        kpiDefinitionRevisionId: field.kpiDefinitionRevisionId,
        kpiDefinitionRevisionRowVersion: field.kpiDefinitionRevisionRowVersion,
        languageId: translation?.languageId ?? 0,
        name: translation?.name ?? field.kpi.name,
        description: translation?.description ?? field.kpi.description,
        instruction: translation?.instruction ?? field.kpi.instruction,
        expectedResult: translation?.expectedResult ?? field.kpi.expectedResult,
        // Only a success/error definition keeps prose in defValue - it is the formula of a computed
        // field, the default choice of a choice list and the pre-filled ANSWER of every other type.
        // A projected criterion is excluded as well: its question is rewritten from the check on the
        // next projection, so it is corrected at the criterion instead of here.
        supportsQuestion: field.kpi.type === KPITypes.SuccesError && !field.criterionKey,
        question: field.kpi.defValue,
      });

      for (const choice of field.kpi.choices ?? []) {
        const choiceTranslation = choice.trans[0];
        const choiceKey = choice.stringValueKey ?? choice.stringValueId;
        const choiceValue = choiceTranslation?.value ?? choice.value;
        targets.push({
          key: `${definitionKey}:choice:${choiceKey}`,
          type: 'choice',
          label: t('forms:Versioning.Correction.KPIChoice', { name: choiceValue || choiceKey }),
          kpiId: field.kpiId,
          kpiDefinitionRevisionId: field.kpiDefinitionRevisionId,
          kpiDefinitionRevisionRowVersion: field.kpiDefinitionRevisionRowVersion,
          choiceKey,
          languageId: choiceTranslation?.languageId ?? 0,
          name: '',
          choiceValue,
          description: choiceTranslation?.description ?? choice.description,
        });
      }

      continue;
    }

    if (field.fieldKey) {
      const translation = field.trans[0];
      targets.push({
        key: `field:${field.fieldKey}`,
        type: 'field',
        label: t('forms:Versioning.Correction.Field', { name: getFieldLabel(field) }),
        fieldKey: field.fieldKey,
        languageId: translation?.languageId ?? 0,
        name: translation?.name ?? field.name,
        description: translation?.description ?? field.description,
        instruction: translation?.instruction ?? field.instruction,
        expectedResult: translation?.expectedResult ?? field.expectedResult,
      });
    }
  }

  return targets;
};

const FormTextCorrectionModal = (props: IFormTextCorrectionModalProps) => {
  const { t } = useTranslation(['forms']);
  const appContext = useContext(AppContext);
  const targets = getCorrectionTargets(props.taskType, t);
  const initialTarget = targets[0];
  const [targetKey, setTargetKey] = useState<string>(formTargetKey);
  const [name, setName] = useState<string>(initialTarget.name);
  const [description, setDescription] = useState<string>(initialTarget.description ?? '');
  const [tabName, setTabName] = useState<string>(initialTarget.tabName ?? '');
  const [instruction, setInstruction] = useState<string>(initialTarget.instruction ?? '');
  const [expectedResult, setExpectedResult] = useState<string>(initialTarget.expectedResult ?? '');
  const [choiceValue, setChoiceValue] = useState<string>(initialTarget.choiceValue ?? '');
  const [question, setQuestion] = useState<string>(initialTarget.question ?? '');
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [impact, setImpact] = useState<TextCorrectionImpactPreview | undefined>(undefined);
  const [isImpactStale, setIsImpactStale] = useState<boolean>(false);
  const correctionMutationIdRef = useRef(newGuid());
  const target = targets.find((item) => item.key === targetKey) ?? initialTarget;
  const isChoiceCorrection = target.type === 'choice';
  const isFormCorrection = target.type === 'form';
  // A form or field text lives on THIS version only: the server previews and writes the one selected
  // version, and sibling versions carrying the same words keep them. A KPI definition or choice text
  // lives on the pinned definition revision, which every form version pinning it reads - so that
  // correction fans out over families and versions, and only there are those counts worth showing.
  const isVersionScoped = isFormCorrection || target.type === 'field';
  const targetOptions = targets.map((item) => ({ key: item.key, text: item.label }));

  // Typing changes what will be saved, so a reviewed impact hash no longer covers it - but WHICH
  // versions, tasks and reports are touched does not depend on the typed text, so the impact stays
  // on screen and only the confirmation step is re-armed.
  const invalidatePreview = () => {
    setIsImpactStale(true);
    correctionMutationIdRef.current = newGuid();
  };

  // Another target is another impact, so there the shown numbers really are gone.
  const resetPreview = () => {
    setImpact(undefined);
    setIsImpactStale(false);
    correctionMutationIdRef.current = newGuid();
  };

  const selectTarget = (nextTargetKey: string) => {
    const nextTarget = targets.find((item) => item.key === nextTargetKey) ?? initialTarget;
    setTargetKey(nextTarget.key);
    setName(nextTarget.name);
    setDescription(nextTarget.description ?? '');
    setTabName(nextTarget.tabName ?? '');
    setInstruction(nextTarget.instruction ?? '');
    setExpectedResult(nextTarget.expectedResult ?? '');
    setChoiceValue(nextTarget.choiceValue ?? '');
    setQuestion(nextTarget.question ?? '');
    resetPreview();
  };

  const createFormCorrectionMutation = (expectedImpactHash?: string): FormTextCorrectionMutation => {
    const mutation = new FormTextCorrectionMutation();
    mutation.taskTypeFamilyId = props.taskType.taskTypeFamilyId ?? 0;
    mutation.taskTypeId = props.taskType.taskTypeId;
    mutation.fieldKey = target.fieldKey;
    mutation.languageId = target.languageId;
    mutation.name = name;
    mutation.description = description;
    mutation.expectedTaskTypeRowVersion = props.taskType.rowVersion;
    mutation.expectedImpactHash = expectedImpactHash;
    mutation.mutationId = correctionMutationIdRef.current;
    if (target.type === 'field') {
      mutation.instruction = instruction;
      mutation.expectedResult = expectedResult;
    } else {
      mutation.tabName = tabName;
    }

    return mutation;
  };

  const createKPIDefinitionCorrectionMutation = (expectedImpactHash?: string): KPIDefinitionTextCorrectionMutation => {
    const mutation = new KPIDefinitionTextCorrectionMutation();
    mutation.kpiId = target.kpiId ?? 0;
    mutation.kpiDefinitionRevisionId = target.kpiDefinitionRevisionId ?? 0;
    mutation.choiceKey = target.choiceKey;
    mutation.languageId = target.languageId;
    mutation.name = isChoiceCorrection ? undefined : name;
    mutation.description = description;
    mutation.instruction = isChoiceCorrection ? undefined : instruction;
    mutation.expectedResult = isChoiceCorrection ? undefined : expectedResult;
    mutation.choiceValue = isChoiceCorrection ? choiceValue : undefined;
    // Sent only when this correction actually rewrites the question: the server leaves defValue alone
    // when it is absent, so correcting the name of a success/error definition never touches it.
    mutation.question = target.supportsQuestion && question !== (target.question ?? '') ? question : undefined;
    mutation.expectedDefinitionRevisionRowVersion = target.kpiDefinitionRevisionRowVersion;
    mutation.expectedImpactHash = expectedImpactHash;

    return mutation;
  };

  const previewCorrection = async () => {
    if (!target.languageId) {
      return;
    }

    try {
      setIsSaving(true);
      const accessToken = await appContext.getAccessToken(apiRequest.scopes);
      const preview =
        target.type === 'form' || target.type === 'field'
          ? await apiPreviewFormTextCorrection(createFormCorrectionMutation(), accessToken)
          : await apiPreviewKPIDefinitionTextCorrection(
              createKPIDefinitionCorrectionMutation(),
              accessToken,
              correctionMutationIdRef.current,
            );
      setImpact(preview);
      setIsImpactStale(false);
    } catch (error) {
      appContext.setError(error);
    } finally {
      setIsSaving(false);
    }
  };

  const correct = async () => {
    if (!target.languageId || !impact || isImpactStale) {
      return;
    }

    try {
      setIsSaving(true);
      const accessToken = await appContext.getAccessToken(apiRequest.scopes);
      let correctedTaskType = props.taskType;
      if (target.type === 'form' || target.type === 'field') {
        correctedTaskType = await apiCorrectFormText(createFormCorrectionMutation(impact.impactHash), accessToken);
      } else {
        await apiCorrectKPIDefinitionText(
          createKPIDefinitionCorrectionMutation(impact.impactHash),
          accessToken,
          correctionMutationIdRef.current,
        );
      }

      await props.onCorrected(correctedTaskType);
      props.onDismiss();
    } catch (error) {
      if (error instanceof AppError && error.code === '409') {
        invalidatePreview();
      }
      appContext.setError(error);
    } finally {
      setIsSaving(false);
    }
  };

  const save = async () => {
    if (impact && !isImpactStale) {
      await correct();
    } else {
      await previewCorrection();
    }
  };

  const getTaskStateText = (taskStateId: number): string => {
    switch (taskStateId) {
      case TaskStateId.Requested:
        return t('forms:Versioning.Correction.Impact.States.Requested');
      case TaskStateId.Accepted:
        return t('forms:Versioning.Correction.Impact.States.Accepted');
      case TaskStateId.InProgress:
        return t('forms:Versioning.Correction.Impact.States.InProgress');
      case TaskStateId.Completed:
        return t('forms:Versioning.Correction.Impact.States.Completed');
      case TaskStateId.Rejected:
        return t('forms:Versioning.Correction.Impact.States.Rejected');
      case TaskStateId.OnHold:
        return t('forms:Versioning.Correction.Impact.States.OnHold');
      case TaskStateId.Archived:
        return t('forms:Versioning.Correction.Impact.States.Archived');
      default:
        return t('forms:Versioning.Correction.Impact.States.Unknown');
    }
  };

  return (
    <GenericModal
      title={t('forms:Versioning.Correction.Title')}
      isOpen={props.isOpen}
      onClose={props.onDismiss}
      onSave={save}
      keepOpenAfterSave={true}
      isActionPending={isSaving}
      saveDisabled={
        !target.languageId ||
        (isChoiceCorrection ? !choiceValue.trim() : !name.trim()) ||
        // Emptying a question is a change of what the definition asks, not a correction of it, and an
        // editor holding only empty markup does not read as empty to the server.
        (target.supportsQuestion === true && !removeHtmlTags(question).trim())
      }
      saveButtonText={
        impact && !isImpactStale ? t('forms:Versioning.Correction.Save') : t('forms:Versioning.Correction.ReviewImpact')
      }
      minWidth={360}
      maxWidth={620}
      // Fixed size: the impact section appearing, disappearing or going stale must not resize the
      // modal. The body scrolls on its own below, so title and footer buttons always stay in view.
      width={'min(94vw, 620px)'}
      height={'min(86vh, 700px)'}
      titleSeparator
    >
      <IPStack verticalFill styles={{ root: { height: '100%', minHeight: 0 } }}>
        <ScrollableStackItem minHeight={100}>
          <IPStack tokens={globalStackTokensGapSmall}>
            <IPDropdown
              label={t('forms:Versioning.Correction.Target')}
              options={targetOptions}
              selectedKey={targetKey}
              onChange={(_, option) => selectTarget(String(option?.key ?? formTargetKey))}
            />
            {/* Says up front how far the correction reaches, before the impact numbers do: a typo
                that sits in three versions of a form is corrected three times, once per version. */}
            <IPText variant="small">
              {isVersionScoped
                ? t('forms:Versioning.Correction.ScopeVersion', { version: props.taskType.version })
                : t('forms:Versioning.Correction.ScopeDefinition')}
            </IPText>
            {isChoiceCorrection ? (
              <IPTextField
                required
                label={t('forms:Versioning.Correction.ChoiceValue')}
                value={choiceValue}
                maxLength={512}
                onChange={(_, value) => {
                  setChoiceValue(value ?? '');
                  invalidatePreview();
                }}
              />
            ) : (
              <IPTextField
                required
                label={t('forms:Versioning.Correction.Name')}
                value={name}
                maxLength={512}
                onChange={(_, value) => {
                  setName(value ?? '');
                  invalidatePreview();
                }}
              />
            )}
            {/* The question is rich text - the field editor writes it with this same editor - so a
                plain text box here would strip its markup. It carries no language of its own, which
                the line underneath says out loud. */}
            {target.supportsQuestion && (
              <IPStack.Item>
                <IPText block>{t('forms:Versioning.Correction.Question')}</IPText>
                <RichTextEditorMCE
                  html={question}
                  maxLength={1024}
                  maxHeight={200}
                  compactToolbar={true}
                  disallowFullscreen={true}
                  disableAutoFocus={true}
                  onChange={(value) => {
                    setQuestion(value);
                    invalidatePreview();
                  }}
                />
                <IPText variant="small">{t('forms:Versioning.Correction.QuestionAllLanguages')}</IPText>
              </IPStack.Item>
            )}
            <IPTextField
              label={t('forms:Versioning.Correction.Description')}
              value={description}
              multiline
              autoAdjustHeight
              maxLength={1024}
              onChange={(_, value) => {
                setDescription(value ?? '');
                invalidatePreview();
              }}
            />
            {isFormCorrection && (
              <IPTextField
                label={t('forms:Versioning.Correction.TabName')}
                value={tabName}
                maxLength={32}
                onChange={(_, value) => {
                  setTabName(value ?? '');
                  invalidatePreview();
                }}
              />
            )}
            {!isFormCorrection && !isChoiceCorrection && (
              <>
                <IPTextField
                  label={t('forms:Versioning.Correction.Instruction')}
                  value={instruction}
                  multiline
                  autoAdjustHeight
                  maxLength={1024}
                  onChange={(_, value) => {
                    setInstruction(value ?? '');
                    invalidatePreview();
                  }}
                />
                <IPTextField
                  label={t('forms:Versioning.Correction.ExpectedResult')}
                  value={expectedResult}
                  multiline
                  autoAdjustHeight
                  maxLength={1024}
                  onChange={(_, value) => {
                    setExpectedResult(value ?? '');
                    invalidatePreview();
                  }}
                />
              </>
            )}
            {impact && (
              <IPStack tokens={globalStackTokensGapSmall}>
                <IPSeparator />
                <IPText block styles={globalTextStylesBold}>
                  {t('forms:Versioning.Correction.Impact.Title')}
                </IPText>
                {isVersionScoped ? (
                  // One family, one version and no KPI data by construction, so only the task count
                  // says anything - named with the version it belongs to.
                  <IPText>
                    {t('forms:Versioning.Correction.Impact.SummaryVersion', {
                      tasks: impact.affectedTaskCount,
                      version: props.taskType.version,
                    })}
                  </IPText>
                ) : (
                  <Fragment>
                    <IPText>
                      {t('forms:Versioning.Correction.Impact.Summary', {
                        families: impact.affectedFamilyCount,
                        versions: impact.formVersions.length,
                        tasks: impact.affectedTaskCount,
                        data: impact.affectedKPIDataCount,
                      })}
                    </IPText>
                    <ul style={{ margin: 0 }}>
                      {impact.formVersions.map((version) => (
                        <li key={version.taskTypeId}>
                          {t('forms:Versioning.Correction.Impact.FormVersion', {
                            name: version.name ?? t('forms:Versioning.UnnamedField'),
                            version: version.version,
                            tasks: version.taskCount,
                            status: version.isCurrent
                              ? t('forms:Versioning.Correction.Impact.Current')
                              : version.isArchived
                              ? t('forms:Versioning.Correction.Impact.Archived')
                              : '',
                          })}
                        </li>
                      ))}
                    </ul>
                  </Fragment>
                )}
                {impact.taskStates.length > 0 && (
                  <IPStack>
                    <IPText>{t('forms:Versioning.Correction.Impact.TaskStates')}</IPText>
                    <ul style={{ margin: 0 }}>
                      {impact.taskStates.map((state) => (
                        <li key={state.taskStateId}>
                          {t('forms:Versioning.Correction.Impact.TaskState', {
                            state: getTaskStateText(state.taskStateId),
                            count: state.taskCount,
                          })}
                        </li>
                      ))}
                    </ul>
                  </IPStack>
                )}
                <IPText>
                  {t('forms:Versioning.Correction.Impact.Reports', {
                    draft: impact.draftReportCount,
                    published: impact.publishedReportCount,
                  })}
                </IPText>
                {impact.draftReportCount > 0 && (
                  <IPText>{t('forms:Versioning.Correction.Impact.DraftReportsStale')}</IPText>
                )}
                {impact.publishedReportCount > 0 && (
                  <IPText>{t('forms:Versioning.Correction.Impact.PublishedReportsFrozen')}</IPText>
                )}
              </IPStack>
            )}
          </IPStack>
        </ScrollableStackItem>
      </IPStack>
    </GenericModal>
  );
};

export default FormTextCorrectionModal;
